import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useConfirmationDialog } from 'src/components/ConfirmationDialog/useConfirmationDialog';
import { FloorChips } from 'src/components/FloorChips/FloorChips';
import { FloorMap } from 'src/components/FloorMap/FloorMap';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { RenderBookingObjectParams } from 'src/components/FloorMap/types';
import { fullscreenIsSupported } from 'src/consts';
import { getAgentState, getIsDesktop } from 'src/store/agent/selectors';
import { getParkingState } from 'src/store/parking/selectors';
import { openParking } from 'src/store/parking/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserPermissions } from 'src/store/user/selectors';
import { Parking } from 'src/types';

import { useFloorChips } from '../../components/FloorChips/FloorChipsContext';

import { BookingParkingPlace } from './BookingParkingPlace/BookingParkingPlace';

export const renderBookingObject = ({
  bookingObject,
}: RenderBookingObjectParams<Parking>): ReactNode => {
  return (
    <BookingParkingPlace key={bookingObject.mapPlaceId} item={bookingObject} />
  );
};

export const ParkingFloorMap: FC = () => {
  const data = useSelector(getUserPermissions);
  const { touch } = useSelector(getAgentState);
  const isDesktop = useSelector(getIsDesktop);
  const dispatch = useAppDispatch();
  const { scrollTo } = useFloorMap();
  const { locationLoading, loading, bookingObjects } =
    useSelector(getParkingState);
  const parkingPlaces = bookingObjects as Map<string, Parking>;

  const { activeFloor, setActiveFloor, items } = useFloorChips();

  const chips = useMemo((): ReactNode => {
    return (
      <FloorChips
        activeFloor={activeFloor}
        setActiveFloor={setActiveFloor}
        floors={items}
        testId='parkingFloors'
      />
    );
  }, [activeFloor, setActiveFloor, items]);

  const {
    dialog: bookDialog,
    open: openBookDialog,
    confirmation,
  } = useConfirmationDialog({
    title: 'Место не входит в твой блок',
    okLabel: 'К бронированию',
    cancelLabel: 'Назад',
    text: 'Бронирование данного места должно быть согласовано с ответственным по блоку',
  });

  const book = useCallback(
    async (id: string) => {
      const nextParking = bookingObjects.get(id) as Parking | undefined;

      if (!nextParking) return;

      if (
        data?.adminSpaces.size &&
        nextParking.spaceId &&
        !data.adminSpaces.has(nextParking.spaceId) &&
        !nextParking.status
      ) {
        openBookDialog();
        const confirmed = await confirmation();
        if (!confirmed) return;
      }

      dispatch(openParking(id));

      if (isDesktop) scrollTo(nextParking);
    },
    [
      bookingObjects,
      confirmation,
      data?.adminSpaces,
      dispatch,
      isDesktop,
      openBookDialog,
      scrollTo,
    ],
  );

  return (
    <>
      {bookDialog}
      <FloorMap
        loading={loading || locationLoading}
        bookingObjects={parkingPlaces}
        onClickOnBookingObject={book}
        footer={isDesktop ? chips : null}
        renderBookingObject={renderBookingObject}
        touchDevice={touch}
        fullscreenIsSupported={fullscreenIsSupported && isDesktop}
      />
    </>
  );
};
